import type {Editor} from 'grapesjs';
import {isUserPremium} from '@Libraries/user.library';
import {getUniqueId} from '@Utils/string.util';
import {EmailCampaignAiRewriteSource} from '@Libraries/ai-email-campaign-content-library';
import {storeAppliedPaletteInHtml} from '@Libraries/auto-branding-email-maker';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import type {SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {EmojiUsage, PromptOption, RedesignMood} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {initiatePosterMorphLoading, terminatePosterMorphLoading} from '@PosterWhiteboard/poster/poster-init';
import type {AutoUpdateEmailContentParams, EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import {EMAIL_OVERLAY_ANIMATION_TIME_MS} from '@EmailMakerReact/email-maker-react.constants';
import type {CanUseBypassedEmailResponse} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {OnLoadUrlParamsConfig} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {
  aiRegenerateEmailTemplateImages,
  aiRewriteEmailCampaignTextComponents,
  applyAutoBrandingStyle,
  toSelectedPromptOptionValues,
} from '@EmailMakerReact/lib/email-maker-ai-library';
import {toggleLoadingOverlayForEmail} from '@EmailMakerReact/lib/email-maker-overlay-library';
import {getEditableHtml} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {refreshSelection} from '@EmailMakerReact/lib/email-maker-selection-library';
import {getCurrentIdEmailTemplate} from '@EmailMakerReact/lib/email-maker-storage-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

const AUTO_UPDATE_LOADING_TOAST_KEY = 'updatingTemplateText';
const AUTO_UPDATE_BRANDING_LOADING_TOAST_KEY = 'updatingTemplateStyles';

/** Maps the PHP prompt-option keys (tone/purpose/useEmojis) onto the prompt-box config option keys. */
export function formatAutoUpdatePromptOptions(promptOptions: SelectedValueType): SelectedValueType {
  return {
    [PromptBoxConfigOptionType.EMAIL_CAMPAIGN_SUBJECT_TONE]: promptOptions.tone,
    [PromptBoxConfigOptionType.PURPOSE]: promptOptions.purpose,
    [PromptBoxConfigOptionType.EMOJIS]: promptOptions.useEmojis,
  };
}

/** Runs the on-load AI rewrite + image regeneration + auto-branding when the load URL requested it. */
export async function applyAutoUpdates(editor: Editor, store: EmailMakerImmutableStore): Promise<void> {
  const autoUpdateEmailContentParams = store.autoUpdateEmailContentParams;

  if (autoUpdateEmailContentParams.autoUpdateEmailTemplateOnLoad) {
    await rewriteTextContentOnLoad(editor, autoUpdateEmailContentParams);
    await applyAutoBrandingOnLoad(editor, autoUpdateEmailContentParams);
  }
}

/** Strips the auto-update query params from the address bar after they have been consumed. */
export function removeAutoUpdateParamsFromUrl(): void {
  const paramsToRemove: string[] = [OnLoadUrlParamsConfig.AutoUpdateEmailContentParams];
  const url = new URL(window.location.href);
  const allParams = url.searchParams;

  paramsToRemove.forEach((param) => {
    allParams.delete(param);
  });

  const newUrl = `${url.origin}${url.pathname}${allParams.toString() ? '?' + allParams.toString() : ''}`;
  window.history.replaceState(null, '', newUrl);
}

/** Whether the editor may bypass ownership checks for the current template. */
async function canBypassOwnershipChecks(editor: Editor): Promise<boolean> {
  try {
    const canUseBypassedEmail = (await window.PMW.readLocal('emailmaker/canUseBypassedEmail', {
      idEmailTemplate: getCurrentIdEmailTemplate(editor),
    })) as CanUseBypassedEmailResponse;

    if (!canUseBypassedEmail) {
      return false;
    }

    return canUseBypassedEmail.canUseEmail;
  } catch (e: unknown) {
    logEmailMakerError('canBypassOwnershipChecks: transport failure', e);
    return false;
  }
}

/** Premium-gated on-load AI rewrite + image regeneration with fixed prompt options. */
async function rewriteTextContentOnLoad(editor: Editor, autoUpdateEmailContentParams: AutoUpdateEmailContentParams): Promise<void> {
  const autoUpdateTextContentParams = autoUpdateEmailContentParams.autoUpdateTextContentParams;

  if (!autoUpdateTextContentParams) {
    return;
  }

  const canBypassOwnership = await canBypassOwnershipChecks(editor);

  if (!isUserPremium() && !canBypassOwnership) {
    await window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT);
    return;
  }
  initiatePosterMorphLoading(AUTO_UPDATE_LOADING_TOAST_KEY);

  toggleLoadingOverlayForEmail(editor, true);
  try {
    await aiRewriteEmailCampaignTextComponents(editor, {
      prompt: autoUpdateTextContentParams.rewriteContentPrompt,
      selectedPromptOptionValues: toSelectedPromptOptionValues({
        [PromptOption.MOOD]: RedesignMood.Formal,
        [PromptOption.EMOJI_USAGE]: EmojiUsage.Disabled,
      }),
      usageSource: EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REDESIGN,
      contentKey: getUniqueId(),
    });
  } catch (e: unknown) {
    logEmailMakerError('rewriteTextContentOnLoad: text rewrite failed', e);
  }

  toggleLoadingOverlayForEmail(editor, false);

  try {
    await aiRegenerateEmailTemplateImages(editor, autoUpdateTextContentParams.rewriteContentPrompt);
  } catch (e: unknown) {
    logEmailMakerError('rewriteTextContentOnLoad: image regeneration failed', e);
  } finally {
    terminatePosterMorphLoading(AUTO_UPDATE_LOADING_TOAST_KEY);
  }
}

/** Applies the requested palette on load, then refreshes the selection once the overlay animation completes. */
async function applyAutoBrandingOnLoad(editor: Editor, autoUpdateEmailContentParams: AutoUpdateEmailContentParams): Promise<void> {
  const autoUpdateBrandingParams = autoUpdateEmailContentParams.autoUpdateBrandingParams;

  if (!autoUpdateBrandingParams) {
    return;
  }

  const waitDuration = EMAIL_OVERLAY_ANIMATION_TIME_MS;

  initiatePosterMorphLoading(AUTO_UPDATE_BRANDING_LOADING_TOAST_KEY);

  const initialHtml = getEditableHtml(editor);
  const colorPalette: ColorPaletteInfo = {
    colors: autoUpdateBrandingParams.colors,
  };

  await applyAutoBrandingStyle(editor, {
    colorPalette,
    html: initialHtml,
    onSuccess: storeAppliedPaletteInHtml,
  });

  setTimeout(() => {
    refreshSelection(editor);
    terminatePosterMorphLoading(AUTO_UPDATE_BRANDING_LOADING_TOAST_KEY);
  }, waitDuration);
}
